/**
 * Кнопка «Получить из телефонии» на вкладке «Сотрудники».
 *
 * Заменила «Получить из Mango»: сервер отдаёт список телефоний, у которых заданы креды
 * (`GET /employees/telephony-sources`), и подсказки имён от них
 * (`POST /employees/fetch-from-telephony`). Подсказки, как и раньше, только заполняют
 * черновики имён — в базу их пишет кнопка «Сохранить».
 *
 * Логика вынесена из `Settings.jsx`, потому что компоненты в проекте юнит-тестами не покрываются,
 * а подпись кнопки, условие её показа и тексты уведомлений проверять надо. Для одной телефонии Mango
 * всё обязано выглядеть ровно как прежняя кнопка.
 */

const GENERIC_LABEL = 'Получить из телефонии';

/**
 * Ответ `telephony-sources` → `[{ id, label }]`. Не массив (сбой запроса, заглушка) → `null`:
 * «неизвестно», и тогда кнопку показываем — сервер сам ответит, настроено ли что-нибудь.
 */
export const normalizeSources = (raw) => {
  if (!Array.isArray(raw)) {
    return null;
  }

  return raw
    .filter((source) => source && typeof source.id === 'string' && source.id.trim() !== '')
    .map((source) => ({
      id: source.id,
      label: typeof source.label === 'string' && source.label.trim() !== '' ? source.label.trim() : source.id
    }));
};

/**
 * Показывать ли кнопку и с какой подписью.
 *
 * Пока список не загружен — кнопки нет: иначе на стенде без телефонии с каталогом она мелькала бы.
 * Пустой список — кнопки нет тоже: на стендах МегаФона и Primatel прежняя кнопка всегда кончалась
 * ошибкой «не настроен», то есть сработать не могла в принципе.
 */
export const resolveFetchButton = ({ loaded, sources }) => {
  if (!loaded) {
    return { visible: false, label: null };
  }

  if (sources === null || sources === undefined) {
    return { visible: true, label: GENERIC_LABEL };
  }

  if (sources.length === 0) {
    return { visible: false, label: null };
  }

  if (sources.length === 1) {
    return { visible: true, label: `Получить из ${sources[0].label}` };
  }

  return { visible: true, label: GENERIC_LABEL };
};

/** Подсказки → новые черновики имён (карточка → имя из телефонии). */
export const applySuggestions = (drafts, suggestions) => {
  const next = { ...drafts };
  for (const item of Array.isArray(suggestions) ? suggestions : []) {
    if (item?.employeeId) {
      next[item.employeeId] = item?.name ?? '';
    }
  }

  return next;
};

const joinLabels = (labels) => {
  const unique = [...new Set(labels.filter(Boolean))];
  if (unique.length <= 1) {
    return unique[0] ?? null;
  }

  return `${unique.slice(0, -1).join(', ')} и ${unique[unique.length - 1]}`;
};

const labelOf = (sources, id) => sources?.find((source) => source.id === id)?.label ?? id;

/** Подпись для текстов: одна телефония — её имя, несколько — перечисление, неизвестно — «телефонии». */
const sourcesLabel = (sources) => (Array.isArray(sources) && sources.length > 0
  ? joinLabels(sources.map((source) => source.label))
  : null);

/** Текст сбоя одного источника по его `kind` (сервер не отдаёт ни ключей, ни тел ответов вендора). */
export const describeFailure = (failure) => {
  const label = failure?.label || failure?.source || 'Телефония';
  switch (failure?.kind) {
    case 'ip_not_whitelisted':
      return `${label}: IP сервера не в белом списке API. Добавьте его в кабинете: Администратор → Аккаунт → Правила и настройки безопасности → API.`;
    case 'auth':
      return `${label} отверг ключ API — проверьте ключ в .env сервера.`;
    case 'forbidden':
      return `${label}: у ключа нет доступа к списку сотрудников.`;
    case 'rate_limited':
      return `${label}: исчерпан лимит запросов к API, повторите позже.`;
    case 'not_configured':
      return `${label}: креды не заданы на сервере.`;
    default:
      return failure?.message || `${label} не ответил.`;
  }
};

/**
 * Уведомление по успешному ответу. `variant` задан только для частичного сбоя, когда часть
 * телефоний ответила, а часть — нет.
 */
export const describeFetchResult = (result, sources) => {
  const suggestions = Array.isArray(result?.suggestions) ? result.suggestions : [];
  const failures = Array.isArray(result?.failures) ? result.failures : [];
  const failureText = failures.map(describeFailure).join(' ');
  const answered = (sources ?? []).filter(
    (source) => !failures.some((failure) => failure?.source === source.id)
  );
  const where = sourcesLabel(answered.length > 0 ? answered : sources);

  if (suggestions.length === 0) {
    const base = where
      ? `В ${where} не найдено сотрудников с внешним ID из справочника.`
      : 'В телефонии не найдено сотрудников с внешним ID из справочника.';
    return {
      title: 'Совпадений не найдено',
      description: failureText ? `${base} ${failureText}` : base,
      variant: failureText ? 'destructive' : undefined
    };
  }

  const from = joinLabels(suggestions.map((item) => labelOf(sources, item?.source))) ?? where ?? 'телефонии';
  const base = `Имена заполнены из ${from}. Нажмите «Сохранить» для записи в базу.`;
  return {
    title: `Имена получены из ${from}`,
    description: failureText ? `${base} ${failureText}` : base,
    variant: failureText ? 'destructive' : undefined
  };
};

/** Уведомление по ошибке запроса. Для одной телефонии Mango тексты — прежние. */
export const describeFetchError = (error, sources) => {
  const status = error?.response?.status;

  if (status === 503) {
    const label = Array.isArray(sources) && sources.length === 1 ? sources[0].label : null;
    return label
      ? { title: `${label} API не настроен`, description: `${label} API не настроен на сервере.` }
      : { title: 'Телефония не настроена', description: 'Ни у одной телефонии на сервере не заданы креды.' };
  }

  const failures = error?.response?.data?.failures;
  if (status === 502 && Array.isArray(failures) && failures.length > 0) {
    return { title: 'Не удалось получить сотрудников', description: failures.map(describeFailure).join(' ') };
  }

  return {
    title: 'Не удалось получить сотрудников',
    description: error?.response?.data?.detail ?? error?.message ?? 'Повторите попытку позже.'
  };
};
